// Single source of truth for where a signed-in user belongs.
//
// Resolution order:
//   1. Explicit ?redirect=... param (e.g. claim-listing, "heart this property")
//   2. Pending anonymous draft → finish the listing flow
//   3. Brand-new owner (intent set, no listings yet) → /owner-onboarding
//   4. Existing owner → /dashboard
//   5. Renter (by intent or existing role) → /properties
//   6. Fallback to /onboarding (the only path that triggers the role wizard)

import type { Intent } from "./onboardingIntent";

export interface ResolveOpts {
  types?: string[];
  intent?: Intent | null;
  redirect?: string | null;
  hasDraft?: boolean;
}

const isOwner = (types: string[]) =>
  types.includes("owner") || types.includes("property_manager");

export const resolvePostAuthRoute = ({
  types = [],
  intent = null,
  redirect = null,
  hasDraft = false,
}: ResolveOpts): string => {
  // A path on this site only: "//host" would be another site.
  if (redirect && redirect.startsWith("/") && !redirect.startsWith("//")) return redirect;

  // Pending anonymous draft always wins, finish what they started.
  if (hasDraft) return "/list-property/new";

  // Brand-new owner who just clicked "List your home" → owner onboarding
  // (create account → profile → listing wizard). Distinguished from a
  // returning owner by the presence of an intent flag, which is cleared once
  // their role is persisted.
  if (intent === "owner" && !isOwner(types)) return "/owner-onboarding";

  // Returning owner → their dashboard (the centralized owner hub).
  if (isOwner(types)) return "/dashboard";

  // Renter (by intent or existing role) → browse homes.
  if (intent === "renter" || types.includes("renter")) return "/properties";

  return "/onboarding";
};
